/**
 * Status mapping utilities for frontend-backend compatibility
 *
 * The frontend uses "ACKNOWLEDGED" for UX clarity, but the backend uses "ACCEPTED"
 * This utility provides bidirectional mapping between frontend and backend status values
 */

export type FrontendOrderStatus =
  | 'PENDING'
  | 'ACKNOWLEDGED'  // Frontend-only, maps to ACCEPTED in backend
  | 'ACCEPTED'      // Backend status
  | 'IN_PROGRESS'
  | 'PREPARING'
  | 'READY'
  | 'SERVED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export type BackendOrderStatus =
  | 'PENDING'
  | 'ACCEPTED'      // Backend equivalent of ACKNOWLEDGED
  | 'IN_PROGRESS'
  | 'PREPARING'
  | 'READY'
  | 'SERVED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

/**
 * Map frontend status to backend status
 * Converts "ACKNOWLEDGED" to "ACCEPTED" for API calls
 */
export function mapStatusToBackend(frontendStatus: string): string {
  if (frontendStatus === 'ACKNOWLEDGED') {
    return 'ACCEPTED';
  }
  return frontendStatus;
}

/**
 * Map backend status to frontend status
 * Converts "ACCEPTED" to "ACKNOWLEDGED" for display
 */
export function mapStatusFromBackend(backendStatus: string): string {
  if (backendStatus === 'ACCEPTED') {
    return 'ACKNOWLEDGED';
  }
  return backendStatus;
}

/**
 * Check if a status is a terminal state (cannot transition out of)
 */
export function isTerminalStatus(status: string): boolean {
  return status === 'COMPLETED' || status === 'CANCELLED';
}

/**
 * Get valid next statuses for a given current status
 */
export function getValidNextStatuses(currentStatus: string): string[] {
  // Map to backend status for validation
  const backendStatus = mapStatusToBackend(currentStatus);

  // Terminal states cannot transition
  if (isTerminalStatus(backendStatus)) {
    return [];
  }

  // Define valid transitions (using frontend status names)
  const transitions: Record<string, string[]> = {
    'PENDING': ['ACKNOWLEDGED', 'PREPARING', 'READY', 'SERVED', 'CANCELLED'],
    'ACKNOWLEDGED': ['PREPARING', 'READY', 'SERVED', 'CANCELLED'],
    'ACCEPTED': ['PREPARING', 'READY', 'SERVED', 'CANCELLED'], // Backend equivalent
    'IN_PROGRESS': ['PREPARING', 'READY', 'SERVED', 'CANCELLED'],
    'PREPARING': ['READY', 'SERVED', 'CANCELLED'],
    'READY': ['SERVED', 'CANCELLED'],
    'SERVED': ['COMPLETED', 'CANCELLED']
  };

  return transitions[currentStatus] || [];
}
